import React, { useState } from 'react';
import { useCrud } from '../lib/useCrud';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Edit, Trash2, MoreVertical, Briefcase, Phone, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';

const stages = [
  { value: 'new', label: 'New', color: 'bg-blue-500/10 text-blue-500' },
  { value: 'contacted', label: 'Contacted', color: 'bg-cyan-500/10 text-cyan-500' },
  { value: 'proposal', label: 'Proposal', color: 'bg-purple-500/10 text-purple-500' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-amber-500/10 text-amber-500' },
  { value: 'won', label: 'Won', color: 'bg-emerald-500/10 text-emerald-500' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500/10 text-red-500' },
];

const sourceOptions = ['website', 'referral', 'linkedin', 'exhibition', 'cold_call', 'social_media', 'other'];
const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(parseFloat(amt) || 0);

export const LeadsPage = () => {
  const { data: leads, loading, fetchData, create, update, remove } = useCrud('/leads');
  const confirm = useConfirmation();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('leads.create');
  const canEdit = hasPermission('leads.edit');
  const canDelete = hasPermission('leads.delete');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ company_name: '', contact_person: '', email: '', phone: '', source: 'other', status: 'new', estimated_value: '', notes: '' });

  const filtered = leads.filter(l => {
    if (stageFilter !== 'all' && l.status !== stageFilter) return false;
    if (searchQuery && !l.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) && !l.contact_person?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedLead) {
        await update(selectedLead.id, formData);
        toast.success('Lead updated');
      } else {
        await create(formData);
        toast.success('Lead added');
      }
    } catch { toast.error('Operation failed'); }
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (lead) => {
    const confirmed = await confirm({ title: 'Delete Lead', description: `Are you sure you want to delete lead "${lead.company_name}"? This action cannot be undone.` });
    if (!confirmed) return;
    try { await remove(lead.id); toast.success('Lead deleted'); } catch { toast.error('Delete failed'); }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({ company_name: lead.company_name || '', contact_person: lead.contact_person || '', email: lead.email || '', phone: lead.phone || '', source: lead.source || 'other', status: lead.status || 'new', estimated_value: String(lead.estimated_value || ''), notes: lead.notes || '' });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const moveStage = async (lead, newStage) => {
    try {
      await update(lead.id, { ...lead, status: newStage });
      toast.success(`Moved to ${stages.find(s => s.value === newStage)?.label || newStage}`);
    } catch { toast.error('Failed to update stage'); }
  };

  const resetForm = () => { setFormData({ company_name: '', contact_person: '', email: '', phone: '', source: 'other', status: 'new', estimated_value: '', notes: '' }); setSelectedLead(null); setIsEditing(false); };

  const getStageBadge = (stage) => {
    const s = stages.find(st => st.value === stage) || stages[0];
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const pipelineValue = filtered.filter(l => l.status !== 'lost').reduce((s, l) => s + (parseFloat(l.estimated_value) || 0), 0);
  const wonValue = filtered.filter(l => l.status === 'won').reduce((s, l) => s + (parseFloat(l.estimated_value) || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Lead CRM</h1><p className="text-muted-foreground">Track and manage sales leads</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-lead-btn"><Plus className="h-4 w-4 mr-2" />Add Lead</Button></DialogTrigger>)}
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit Lead' : 'Add Lead'}</DialogTitle><DialogDescription>Enter lead details</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Company *</Label><Input value={formData.company_name} onChange={e => setFormData({ ...formData, company_name: e.target.value })} required data-testid="lead-company" /></div>
                <div className="space-y-2"><Label>Contact Person *</Label><Input value={formData.contact_person} onChange={e => setFormData({ ...formData, contact_person: e.target.value })} required data-testid="lead-contact" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
                <div className="space-y-2"><Label>Source</Label><Select value={formData.source} onValueChange={v => setFormData({ ...formData, source: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{sourceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Stage</Label><Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}><SelectTrigger data-testid="lead-stage"><SelectValue /></SelectTrigger><SelectContent>{stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Deal Value (INR)</Label><Input type="number" value={formData.estimated_value} onChange={e => setFormData({ ...formData, estimated_value: e.target.value })} data-testid="lead-value" /></div>
                <div className="space-y-2"><Label>Notes</Label><Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="lead-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4"><div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search leads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="lead-search" /></div>
        <Select value={stageFilter} onValueChange={setStageFilter}><SelectTrigger className="w-full sm:w-[180px]" data-testid="lead-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger><SelectContent><SelectItem value="all">All Stages</SelectItem>{stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
      </div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{filtered.length}</p><p className="text-xs text-muted-foreground">Total Leads</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{formatCurrency(pipelineValue)}</p><p className="text-xs text-muted-foreground">Pipeline Value</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{formatCurrency(wonValue)}</p><p className="text-xs text-muted-foreground">Won Deals</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><Briefcase className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{filtered.filter(l => l.status === 'negotiation').length}</p><p className="text-xs text-muted-foreground">In Negotiation</p></div></div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(lead => (
          <Card key={lead.id} className="hover:border-primary/30 transition-colors" data-testid={`lead-card-${lead.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div><p className="font-semibold">{lead.company_name}</p><p className="text-sm text-muted-foreground">{lead.contact_person}</p></div>
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`lead-actions-${lead.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && (<DropdownMenuItem onClick={() => handleEdit(lead)} data-testid={`edit-lead-${lead.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                  {canEdit && lead.status !== 'won' && lead.status !== 'lost' && (
                    <>
                      {stages.filter(s => s.value !== lead.status && s.value !== 'lost').map(s => (
                        <DropdownMenuItem key={s.value} onClick={() => moveStage(lead, s.value)}><ArrowRight className="h-4 w-4 mr-2" />Move to {s.label}</DropdownMenuItem>
                      ))}
                    </>
                  )}
                  {canDelete && (<DropdownMenuItem onClick={() => handleDelete(lead)} className="text-destructive focus:text-destructive" data-testid={`delete-lead-${lead.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
                </DropdownMenuContent></DropdownMenu>
              </div>
              <div className="flex items-center gap-3 mb-3 text-sm text-muted-foreground">
                {lead.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /><span className="truncate">{lead.email}</span></div>}
                {lead.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /><span>{lead.phone}</span></div>}
              </div>
              <div className="flex items-center justify-between">
                {getStageBadge(lead.status)}
                <span className="text-sm font-semibold">{formatCurrency(lead.estimated_value)}</span>
              </div>
              {lead.notes && <p className="text-xs text-muted-foreground mt-2 truncate">{lead.notes}</p>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
